import React, { useState } from "react";
import { toast } from "sonner";
import { Shield, Plus, Users } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { themeFor } from "@/lib/communities";

export default function Clans() {
    const { data: communities } = useFetch("/communities");
    const { data: clans, reload } = useFetch("/clans");
    const [filter, setFilter] = useState("ALL");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", tag: "", community_key: "CODERS_PLAYGROUND", description: "" });
    if (!communities || !clans) return <Loader />;
    const upd = (k, v) => setForm({ ...form, [k]: v });
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || k;
    const shown = filter === "ALL" ? clans : clans.filter((c) => c.community_key === filter);

    const create = async () => {
        if (!form.name || !form.tag) { toast.error("Name and tag required"); return; }
        try { await api.post("/clans", form); toast.success("Clan created", { description: form.name }); setOpen(false); setForm({ ...form, name: "", tag: "", description: "" }); reload(); }
        catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    };
    const join = async (c) => { try { const { data } = await api.post(`/clans/${c.id}/join`); toast.success(data.message); reload(); } catch { toast.error("Failed"); } };

    return (
        <div data-testid="player-clans">
            <PageHeader title="Clans" subtitle="Form a squad, recruit members, dominate the wipe."
                action={<Button data-testid="new-clan-btn" onClick={() => setOpen(!open)} className="font-heading uppercase bg-hazard text-black hover:bg-hazard/90"><Plus size={15} className="mr-1" /> Create Clan</Button>} />
            {open && (
                <SectionCard title="Create Clan" className="mb-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input data-testid="clan-name" placeholder="Clan name" value={form.name} onChange={(e) => upd("name", e.target.value)} className="bg-background border-border" />
                        <Input data-testid="clan-tag" placeholder="TAG" value={form.tag} onChange={(e) => upd("tag", e.target.value)} className="bg-background border-border" />
                        <Select value={form.community_key} onValueChange={(v) => upd("community_key", v)}>
                            <SelectTrigger data-testid="clan-community" className="bg-background border-border"><SelectValue /></SelectTrigger>
                            <SelectContent>{communities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Textarea placeholder="Description" value={form.description} onChange={(e) => upd("description", e.target.value)} className="bg-background border-border md:col-span-2" rows={2} />
                    </div>
                    <Button data-testid="clan-create" onClick={create} className="mt-3 font-heading uppercase bg-radioactive/90 text-black hover:bg-radioactive">Create</Button>
                </SectionCard>
            )}
            <div className="flex flex-wrap gap-2 mb-4">
                {["ALL", ...communities.map((c) => c.key)].map((k) => (
                    <button key={k} onClick={() => setFilter(k)} data-testid={`clan-filter-${k}`}
                        className={`px-3 py-1.5 rounded-sm label-mono border ${filter === k ? "border-hazard text-hazard bg-hazard/10" : "border-border text-muted-foreground"}`}>
                        {k === "ALL" ? "ALL" : nameFor(k)}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shown.map((c) => {
                    const t = themeFor({ theme: communities.find((x) => x.key === c.community_key)?.theme });
                    return (
                        <div key={c.id} data-testid={`clan-${c.id}`} className="tactical-card rounded-lg p-5" style={{ borderTop: `3px solid ${t.accent}` }}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Shield size={18} style={{ color: t.accent }} />
                                    <span className="font-heading text-xl font-bold uppercase text-foreground">[{c.tag}] {c.name}</span>
                                </div>
                            </div>
                            <div className="label-mono text-muted-foreground mt-1">{nameFor(c.community_key)}</div>
                            <p className="text-sm text-muted-foreground mt-2">{c.description}</p>
                            <div className="flex items-center justify-between mt-4">
                                <span className="flex items-center gap-1.5 text-sm text-foreground"><Users size={14} className="text-muted-foreground" /> {c.members.length} · Leader {c.leader_name}</span>
                                <Button data-testid={`join-clan-${c.id}`} size="sm" variant="outline" className="border-hazard/40 text-hazard hover:bg-hazard/10" onClick={() => join(c)}>Join</Button>
                            </div>
                        </div>
                    );
                })}
                {shown.length === 0 && <div className="text-muted-foreground">No clans yet. Be the first to create one.</div>}
            </div>
            <Footer />
        </div>
    );
}
